// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Table as TanstackTable } from '@tanstack/react-table';
import moment from 'moment';
import { toast } from 'sonner';
import { FileSpreadsheet, RotateCcw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import { MultiSelect } from '@/components/ui/multi-select';
import Loader from '@/components/partials/components/Loader';
import BusyGuard from '@/components/feedback/BusyGuard';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { ColumnChart, DonutChart, HBarChart, REPORT_COLORS, ReportChartCard, TopNSelect } from '@/components/charts/ReportCharts';
import { downloadXlsx, exportLines, stampedFileName, visibleExportFields } from '@/components/data-table/csv-export';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_AGENTS_LIST, API_HANGUP_CAUSES, API_SCOPED_NUMBERS_LITE, DOWNLOAD_REPORT_CDR } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getUser } from '@/utilities/setting';
import { campaignLabel } from '@/components/partials/components/CallModeTag';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { canReadRecordings } from '@/utilities/recordingsAcl';
import { canCallQualityFeedback } from '@/features/call-quality-feedback/feedback';
import { useCanViewTranscripts, useTranscriptAvailability } from '@/features/transcripts/CallTranscript';
import CdrCallDetailDialog from '@/features/ivr-reports/components/CdrCallDetailDialog';
import { attachExportMedia, REPORT_BATCH_SIZE, REPORT_PAGE_SIZES } from '@/features/ivr-reports/DidReportDetailPage';
import { useCallReportLoader } from './useCallReportLoader';
import {
  buildCallCharts,
  buildCallStats,
  CALL_DURATION_BUCKETS,
  CALL_STATUS_SERIES,
  CALL_STATUS_TEXT,
  callStatusKey,
  talkBucket,
} from './callReportStats';
import { buildCallReportColumns, callGroupOf, callReportExportSplit, numbersMatch } from './callReportColumns';

const MAX_DIDS = 50;
const MAX_RANGE_DAYS = 7;

export const CALL_SEARCH_BY_OPTIONS = [
  { value: 'all', label: 'All', placeholder: 'Call ID, SIP call ID or trace ID' },
  { value: 'call_sid', label: 'Call ID', placeholder: 'Call ID' },
  { value: 'sip_call_id', label: 'SIP Call ID', placeholder: 'SIP call ID' },
  { value: 'trace_id', label: 'Trace ID', placeholder: 'Trace ID' },
  { value: 'customer', label: 'Customer', placeholder: 'Customer number' },
];

const CALL_FILTERS = [
  { value: 'status', label: 'Call Status' },
  { value: 'talk', label: 'Customer Talk Time' },
  { value: 'hangup', label: 'Hangup Cause' },
  { value: 'call_group', label: 'Call Group' },
  { value: 'campaign', label: 'Campaign' },
];

const CARDS = [
  { key: 'total', label: 'Total Calls', className: 'text-blue-600 dark:text-blue-400' },
  { key: 'connected', label: CALL_STATUS_TEXT.connected, className: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'failed', label: CALL_STATUS_TEXT.failed, className: 'text-rose-600 dark:text-rose-400' },
  { key: 'abandoned', label: CALL_STATUS_TEXT.abandoned, className: 'text-amber-600 dark:text-amber-400' },
  { key: 'pseudo', label: 'Pseudo CDRs', className: 'text-yellow-600 dark:text-yellow-400' },
  { key: 'missedIvr', label: 'Missed IVR', className: 'text-rose-600 dark:text-rose-400' },
];

const day = (d: any) => (d ? moment(d).format('YYYY-MM-DD') : '');
const fromDay = (s: any) => (s ? moment(s, 'YYYY-MM-DD').toDate() : null);
const maxEnd = (start: any) => {
  const cap = moment(start).add(MAX_RANGE_DAYS - 1, 'days');
  return (start && cap.isBefore(moment(), 'day') ? cap : moment()).toDate();
};
const rangeError = (start: any, end: any, startTime: string, endTime: string) => {
  const span = moment(end).startOf('day').diff(moment(start).startOf('day'), 'days');
  return !start || !end || span < 0 || span >= MAX_RANGE_DAYS || (span === 0 && startTime > endTime)
    ? `Date range can be at most ${MAX_RANGE_DAYS} days, and From must be on or before To.`
    : '';
};
const fullTime = (v: string, sec: string) => (/^\d{2}:\d{2}$/.test(v) ? `${v}:${sec}` : v);

type Props = {
  lockedDid?: string;
  lockedDirection?: 'incoming' | 'outgoing';
  buildUrl: (query: string, range: { start: string; end: string }) => string;
  traceUrlBase: string;
  initialRange?: { start: Date; end: Date; startTime?: string; endTime?: string };
  persistKey?: string;
  /** Campaign report: no DID filter (the campaign is the scope), campaign-only columns, range kept inside the campaign. */
  campaignMode?: boolean;
  minDate?: Date;
  detailedReport?: boolean;
};

export default function CallReportView({
  lockedDid,
  lockedDirection,
  buildUrl,
  traceUrlBase,
  initialRange,
  persistKey,
  campaignMode,
  minDate,
  detailedReport,
}: Props) {
  const navigate = useNavigate();
  const pickDids = !lockedDid && !campaignMode;
  const pk = (name: string) => (persistKey ? `${persistKey}:${name}` : null);
  const user = useMemo(() => getUser() || {}, []);
  const role = user?.role;
  const defaults = initialRange || { start: moment().subtract(MAX_RANGE_DAYS - 1, 'days').startOf('day').toDate(), end: new Date() };

  const [startDate, setStartDate] = usePersistedState<any>(pk('startDate'), defaults.start);
  const [endDate, setEndDate] = usePersistedState<any>(pk('endDate'), defaults.end);
  const [startTime, setStartTime] = usePersistedState(pk('startTime'), initialRange?.startTime || '00:00:00');
  const [endTime, setEndTime] = usePersistedState(pk('endTime'), initialRange?.endTime || '23:59:59');
  const [selectedDids, setSelectedDids] = usePersistedState<string[]>(pk('selectedDids'), []);
  const [selectedAgents, setSelectedAgents] = usePersistedState<string[]>(pk('selectedAgents'), []);
  const [callDirection, setCallDirection] = usePersistedState(pk('callDirection'), '');
  const [callIdSearch, setCallIdSearch] = usePersistedState(pk('callIdSearch'), '');
  const [callIdSearchBy, setCallIdSearchBy] = usePersistedState(pk('callIdSearchBy'), 'all');
  const [pseudoOnly, setPseudoOnly] = usePersistedState(pk('pseudoOnly'), false);
  const [topAgents, setTopAgents] = usePersistedState(pk('topAgents'), 8);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [didOptions, setDidOptions] = useState<any[]>([]);
  const [agentOptions, setAgentOptions] = useState<any[]>([]);
  const [hangupCauseMap, setHangupCauseMap] = useState<any>({});
  const [detailRow, setDetailRow] = useState<any>(null);
  const [showCharts, setShowCharts] = useState(false);
  const [formError, setFormError] = useState('');
  const loader = useCallReportLoader();
  const { rows, total, loading, loadingMore, stoppedAt, progressText } = loader;
  const canViewRecordings = canReadRecordings();
  const canViewFeedback = canCallQualityFeedback('read');
  const canViewTranscripts = useCanViewTranscripts();

  useEffect(() => {
    getApiCall(API_HANGUP_CAUSES)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        setHangupCauseMap(Object.fromEntries(list.map((i: any) => [i.hangup_cause_code, i.message || i.hangup_cause_reason || ''])));
      })
      .catch(() => setHangupCauseMap({}));
    getApiCall(API_AGENTS_LIST)
      .then((response: any) => {
        const list = Array.isArray(response) ? response : [];
        setAgentOptions(
          list
            .map((a: any) => {
              const username = String(a?.username || '').trim();
              const agentNumber = String(a?.agent_number || '').trim();
              const agentName = String(a?.agent_name || '').trim();
              if (!username && !agentNumber && !agentName) return null;
              return { value: JSON.stringify({ username, agentNumber, agentName }), label: agentName || username || 'Agent' };
            })
            .filter(Boolean),
        );
      })
      .catch(() => setAgentOptions([]));
    if (!pickDids) return;
    getApiCall(API_SCOPED_NUMBERS_LITE)
      .then((response: any) => {
        const list = Array.isArray(response) ? response : response?.data || [];
        const dids = [
          ...new Set(
            list
              .filter((n: any) => ['DID', 'CLI'].includes(String(n?.number_type || '').toUpperCase()) && n?.actual_number)
              .map((n: any) => String(n.actual_number).trim())
              .filter(Boolean),
          ),
        ].map((n) => ({ label: n, value: n }));
        setDidOptions(dids);
        setSelectedDids((prev) => (prev.length ? prev : dids.slice(0, 1).map((o) => o.value)));
      })
      .catch(() => setDidOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedDid]);

  const hangupLabel = useCallback((code: any) => (code ? hangupCauseMap[code] || String(code) : '-'), [hangupCauseMap]);
  const endReason = useCallback((r: any) => (r.hangup_cause ? hangupLabel(r.hangup_cause) : 'Unknown'), [hangupLabel]);

  const serverQuery = () => {
    const err =
      rangeError(startDate, endDate, startTime, endTime) ||
      (pickDids && selectedDids.find((v) => !/^\d{10,15}$/.test(v))
        ? 'Each DID must be numeric, 10-15 digits.'
        : '');
    setFormError(err);
    if (err) return null;
    const range = { start: `${day(startDate)} ${startTime}`, end: `${day(endDate)} ${endTime}` };
    const base = new URLSearchParams({ view: 'calls', limit: String(REPORT_BATCH_SIZE), startDate: range.start, endDate: range.end });
    if (pickDids) base.set('did', selectedDids.join(','));
    const direction = lockedDirection ? '' : callDirection;
    if (direction) base.set('direction', direction);
    if (pseudoOnly) base.set('pseudo', 'only');
    const idSearch = String(callIdSearch || '').trim();
    if (idSearch) {
      base.set('callIdSearch', idSearch);
      if (callIdSearchBy !== 'all') base.set('searchBy', callIdSearchBy);
    }
    if (!pseudoOnly) {
      selectedAgents.forEach((key) => {
        try {
          const a = JSON.parse(key);
          if (a?.agentNumber) base.append('agentNumber', String(a.agentNumber));
          if (a?.agentName) base.append('agentName', String(a.agentName));
        } catch {
          /* bad option value: skip */
        }
      });
    }
    return { range, base };
  };

  const search = () => {
    const query = serverQuery();
    if (!query) return;
    const { range, base } = query;
    loader.start((cursor) => {
      const q = new URLSearchParams(base);
      if (cursor) {
        q.set('beforeMs', String(cursor.ms));
        q.set('beforeSid', cursor.sid || '');
      }
      return buildUrl(q.toString(), range);
    });
  };

  const [queueingReport, setQueueingReport] = useState(false);
  const generateDetailedReport = () => {
    const query = serverQuery();
    if (!query) return;
    const { range, base } = query;
    setQueueingReport(true);
    postApiCall(DOWNLOAD_REPORT_CDR, {
      startDate: range.start,
      endDate: range.end,
      did: base.get('did') || '',
      direction: base.get('direction') || '',
      pseudo: base.get('pseudo') || '',
      callIdSearch: base.get('callIdSearch') || '',
      searchBy: base.get('searchBy') || '',
      agentNumber: base.getAll('agentNumber'),
      agentName: base.getAll('agentName'),
    })
      .then(() =>
        toast.success('Detailed report is being generated. Download it from Download Reports when ready.', {
          action: { label: 'Open', onClick: () => navigate(`/${ROUTES.IVR.DOWNLOAD_REPORT}`) },
        }),
      )
      .catch((err: any) => toast.error(friendlyApiError(err)))
      .finally(() => setQueueingReport(false));
  };

  // First load: DID page at once; CDR page once a DID is picked (as before).
  const firstLoad = useRef(false);
  useEffect(() => {
    if (firstLoad.current || (pickDids && !selectedDids.length)) return;
    firstLoad.current = true;
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedDid, selectedDids]);

  /* ── Browser-side call filter (loaded calls only; server numbers stay as they are). ── */
  const filterOf = useCallback(
    (category: string) =>
      ({
        status: (r: any) => CALL_STATUS_TEXT[callStatusKey(r)],
        talk: talkBucket,
        hangup: (r: any) => (r.hangup_cause ? hangupLabel(r.hangup_cause) : ''),
        call_group: callGroupOf,
        campaign: campaignLabel,
      })[category],
    [hangupLabel],
  );
  const filterValueOptions = useMemo(() => {
    if (filterCategory === 'status') return CALL_STATUS_SERIES.map((s) => s.label);
    if (filterCategory === 'talk') return CALL_DURATION_BUCKETS.map((b) => b.label);
    const get = filterOf(filterCategory);
    return get ? [...new Set(rows.map(get).filter(Boolean))].sort((a: any, b: any) => a.localeCompare(b)) : [];
  }, [filterCategory, rows, filterOf]);
  const filteredRows = useMemo(() => {
    const get = filterOf(filterCategory);
    return get && filterValue ? rows.filter((r) => get(r) === filterValue) : rows;
  }, [rows, filterCategory, filterValue, filterOf]);
  const browserFiltered = filteredRows.length !== rows.length || Boolean(filterCategory && filterValue);

  const stats = useMemo(() => buildCallStats(filteredRows), [filteredRows]);
  const charts = useMemo(() => buildCallCharts(filteredRows, topAgents, endReason), [filteredRows, topAgents, endReason]);
  const transcriptLineCount = useTranscriptAvailability(useMemo(() => filteredRows.map((r: any) => r?.trace_id), [filteredRows]));

  /* ── Export: waits for the rest of the calls while loading. ── */
  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);
  const [exportWaiting, setExportWaiting] = useState(false);
  const exportTableRef = useRef<TanstackTable<any> | null>(null);
  const exportToastRef = useRef<any>(null);
  const releaseExport = () => {
    exportingRef.current = false;
    setExporting(false);
  };
  const cancelPendingExport = () => {
    if (!exportWaiting) return;
    setExportWaiting(false);
    toast.info('Export cancelled: a new search started', { id: exportToastRef.current });
    releaseExport();
  };

  const columnOpts = {
    hangupLabel,
    onView: setDetailRow,
    canViewRecordings,
    canViewFeedback,
    canViewTranscripts,
    transcriptLineCount,
    campaignExtras: campaignMode,
    showPulses: campaignMode && rows.some((r) => Array.isArray(r.pulses_breakdown)),
    showCompanyNames: role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUB_SUPERADMIN,
  };

  const writeExport = async (table: TanstackTable<any>, toastId: any) => {
    const list = table.getPrePaginationRowModel().rows.map((r) => r.original);
    if (!list.length) {
      toast.info('No calls to export', { id: toastId });
      releaseExport();
      return;
    }
    toast.loading(`Exporting ${list.length.toLocaleString()} calls…`, { id: toastId });
    try {
      const media = new Map<any, any[]>();
      let mediaFailed = 0;
      const mediaUrls = (r: any, type: string) =>
        (media.get(r) || []).filter((m) => m.type === type).map((m) => m.url).join(' | ');
      const split = callReportExportSplit(columnOpts, mediaUrls);
      if (pseudoOnly) delete split.pseudo;
      const fields = visibleExportFields(table, split);
      // Media URLs only when that column is exported (reference ids resolved 50 per request).
      if (table.getVisibleLeafColumns().some((c) => c.id === 'recording')) {
        const withMedia = list.map((r) => ({ mainCall: r, relatedCalls: [], media: [] }));
        mediaFailed = await attachExportMedia(withMedia);
        withMedia.forEach((d) => media.set(d.mainCall, d.media));
      }
      await downloadXlsx(exportLines(fields, list), stampedFileName('calls', 'xlsx'));
      if (mediaFailed) {
        toast.warning(`Exported ${list.length} calls; ${mediaFailed} recording lookups failed, those URLs are blank`, { id: toastId });
      } else {
        toast.success(`Exported ${list.length} calls`, { id: toastId });
      }
    } catch (err: any) {
      console.error(err);
      toast.error(`Export failed${err?.message ? `: ${err.message}` : ''}`, { id: toastId });
    } finally {
      releaseExport();
    }
  };

  const onExport = async (table: TanstackTable<any>) => {
    if (exportingRef.current) {
      toast.info('Export already in progress');
      return;
    }
    exportingRef.current = true;
    setExporting(true);
    if (loadingMore || stoppedAt) {
      exportTableRef.current = table;
      exportToastRef.current = toast.loading(`Loading full data for export: ${progressText}`);
      setExportWaiting(true);
      if (!loadingMore) loader.resume();
      return;
    }
    await writeExport(table, toast.loading('Exporting…'));
  };

  useEffect(() => {
    if (!exportWaiting) return;
    const id = exportToastRef.current;
    if (loading) {
      setExportWaiting(false);
      toast.info('Export cancelled: a new search started', { id });
      releaseExport();
      return;
    }
    if (loadingMore) {
      toast.loading(`Loading full data for export: ${progressText}`, { id });
      return;
    }
    setExportWaiting(false);
    if (stoppedAt) {
      toast.error('Export stopped: loading failed before all records arrived. Press Resume loading, then export again.', { id });
      releaseExport();
      return;
    }
    if (exportTableRef.current) writeExport(exportTableRef.current, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportWaiting, loading, loadingMore, stoppedAt, progressText]);

  const handleSearch = () => {
    cancelPendingExport();
    search();
  };

  const handleReset = () => {
    cancelPendingExport();
    const d = initialRange || { start: moment().subtract(MAX_RANGE_DAYS - 1, 'days').startOf('day').toDate(), end: new Date() };
    setStartDate(d.start);
    setEndDate(d.end);
    setStartTime(initialRange?.startTime || '00:00:00');
    setEndTime(initialRange?.endTime || '23:59:59');
    if (!lockedDid) setSelectedDids([]);
    setSelectedAgents([]);
    setCallDirection('');
    setCallIdSearch('');
    setCallIdSearchBy('all');
    setPseudoOnly(false);
    setFilterCategory('');
    setFilterValue('');
    setFormError('');
    loader.cancel();
  };

  const exportGuard = (
    <BusyGuard
      active={exporting}
      title="Please wait, your export is in progress"
      message="We're preparing your report. Leaving, refreshing or closing this page right now would cancel the export. You can continue as soon as your download starts. Thank you for your patience."
    />
  );

  if (loading) return <>{exportGuard}<Loader /></>;

  const columns = buildCallReportColumns(columnOpts);
  const label = 'text-[11px] font-medium uppercase tracking-wide text-muted-foreground';
  const ignored = pseudoOnly ? <span className="ml-1 normal-case tracking-normal text-yellow-600 dark:text-yellow-400">(ignored)</span> : null;
  const statusSeries = CALL_STATUS_SERIES.map((s) => ({ key: s.key, label: s.label, color: s.color }));
  const allLoaded = total ?? rows.length;

  // DID view details: the range is the summary row's window (from the URL), so no date / time inputs.
  const filterBar = (filters: React.ReactNode) =>
    lockedDid ? (
      <div className="flex w-full flex-wrap items-end gap-2">
        {filters}
        <Button type="button" size="sm" onClick={handleSearch}>
          <Search className="h-3.5 w-3.5" />
          Search
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleReset}>
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </Button>
      </div>
    ) : (
      <DateRangeFilter
        className="w-full"
        value={{ from: day(startDate), to: day(endDate) }}
        // 7-day cap: a new From pulls To into its window; a To past the window slides From along.
        onChange={({ from, to }) => {
          const nextFrom = fromDay(from);
          const nextTo = fromDay(to);
          if (from !== day(startDate)) {
            setStartDate(nextFrom);
            if (nextFrom && (!nextTo || moment(nextTo).isBefore(nextFrom, 'day') || moment(nextTo).isAfter(maxEnd(nextFrom), 'day'))) {
              setEndDate(maxEnd(nextFrom));
            } else if (to !== day(endDate)) {
              setEndDate(nextTo);
            }
          } else if (to !== day(endDate)) {
            setEndDate(nextTo);
            if (nextTo && startDate && moment(nextTo).isAfter(maxEnd(startDate), 'day')) {
              setStartDate(moment(nextTo).subtract(MAX_RANGE_DAYS - 1, 'days').startOf('day').toDate());
            }
          }
        }}
        min={day(minDate || moment().subtract(6, 'months').toDate())}
        max={day(new Date())}
        onApply={handleSearch}
        applyLabel="Search"
        onReset={handleReset}
        actions={
          detailedReport ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={queueingReport}
              onClick={generateDetailedReport}
              title="Generate an Excel with call details, call legs, recordings and IVR journey for these filters"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Generate Detailed Report
            </Button>
          ) : undefined
        }
      >
        {filters}
      </DateRangeFilter>
    );

  return (
    <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
      {exportGuard}
      <div className="mb-3 flex flex-wrap items-end gap-2">
        {pickDids && (
          <div className="flex w-full flex-col gap-1">
            <label className={label}>
              DID(s) <span className="normal-case tracking-normal">(optional)</span>
              {pseudoOnly && (
                <span className="ml-2 normal-case tracking-normal text-yellow-600 dark:text-yellow-400">
                  Ignored for pseudo CDRs (they carry no DID)
                </span>
              )}
              {!pseudoOnly && didOptions.length > 0 && (
                <>
                  <button
                    type="button"
                    className="ml-2 normal-case tracking-normal text-primary hover:underline"
                    onClick={() => {
                      if (didOptions.length > MAX_DIDS) {
                        toast.info(`Selected the first ${MAX_DIDS} of ${didOptions.length} DIDs (max ${MAX_DIDS} at a time).`);
                      }
                      setSelectedDids(didOptions.slice(0, MAX_DIDS).map((o) => o.value));
                    }}
                  >
                    Select all{didOptions.length > MAX_DIDS ? ` (first ${MAX_DIDS})` : ''}
                  </button>
                  {selectedDids.length > 0 && (
                    <button
                      type="button"
                      className="ml-2 normal-case tracking-normal text-muted-foreground hover:underline"
                      onClick={() => setSelectedDids([])}
                    >
                      Clear
                    </button>
                  )}
                </>
              )}
            </label>
            <MultiSelect
              options={didOptions}
              value={selectedDids}
              onChange={(next) => {
                if (next.length > MAX_DIDS) {
                  toast.info(`You can select up to ${MAX_DIDS} DIDs at a time.`);
                  return;
                }
                setSelectedDids(next);
              }}
              disabled={pseudoOnly}
              placeholder="Select DID(s)"
              searchPlaceholder="Search DID…"
              emptyMessage="No DIDs available"
              maxChips={10}
              className="w-full max-w-4xl"
            />
          </div>
        )}

        {filterBar(<>
          {!lockedDid && (<div className="flex flex-col gap-1">
            <label className={label}>Time (From day / To day)</label>
            <div className="flex items-center gap-1">
              <input
                type="time"
                step="1"
                aria-label="Start time"
                value={startTime}
                onChange={(e) => setStartTime(fullTime(e.target.value, '00') || '00:00:00')}
                className="input-embossed h-10 w-[130px] rounded-md px-2 text-sm"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                type="time"
                step="1"
                aria-label="End time"
                value={endTime}
                onChange={(e) => setEndTime(fullTime(e.target.value, '59') || '23:59:59')}
                className="input-embossed h-10 w-[130px] rounded-md px-2 text-sm"
              />
            </div>
          </div>)}

          <div className="flex flex-col gap-1">
            <label className={label}>Agent{ignored}</label>
            <MultiSelect
              options={agentOptions}
              value={selectedAgents}
              onChange={(next) => {
                if (next.length > MAX_DIDS) {
                  toast.info(`You can select up to ${MAX_DIDS} agents at a time.`);
                  return;
                }
                setSelectedAgents(next);
              }}
              disabled={pseudoOnly}
              placeholder="All agents"
              searchPlaceholder="Search agent…"
              emptyMessage="No agents available"
              maxChips={2}
              className="w-full sm:w-[260px]"
            />
          </div>

          {!lockedDirection && (
            <div className="flex flex-col gap-1">
              <label className={label}>Direction</label>
              <select
                value={callDirection}
                onChange={(e) => setCallDirection(e.target.value)}
                className="input-embossed h-10 w-[160px] rounded-md px-3 text-sm"
              >
                <option value="">All</option>
                <option value="incoming">Inbound</option>
                <option value="outgoing">Outbound</option>
              </select>
            </div>
          )}

          {/* Pseudo CDRs carry no DID, so they never belong to a locked DID. */}
          {!lockedDid && <div className="flex flex-col gap-1">
            <label className={label}>CDR Type</label>
            <select
              value={pseudoOnly ? 'only' : ''}
              onChange={(e) => setPseudoOnly(e.target.value === 'only')}
              title="Pseudo CDRs carry no DID or agent, so DID / agent filters do not apply to them"
              className="input-embossed h-10 w-[180px] rounded-md px-3 text-sm"
            >
              <option value="">All CDRs</option>
              <option value="only">Pseudo CDRs only</option>
            </select>
          </div>}

          <div className="flex flex-col gap-1">
            <label className={label}>Search</label>
            <div className="flex flex-wrap gap-1">
              <select
                aria-label="Search by"
                value={callIdSearchBy}
                onChange={(e) => setCallIdSearchBy(e.target.value)}
                className="input-embossed h-10 w-[150px] rounded-md px-2 text-sm"
              >
                {CALL_SEARCH_BY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={callIdSearch}
                onChange={(e) => setCallIdSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder={CALL_SEARCH_BY_OPTIONS.find((o) => o.value === callIdSearchBy)?.placeholder}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm sm:w-[320px]"
              />
            </div>
          </div>
        </>)}
        {formError && <span className="w-full text-xs text-destructive">{formError}</span>}
      </div>

      {rows.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className={label}>Filter loaded calls by</label>
              <select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setFilterValue('');
                }}
                className="input-embossed h-9 w-[200px] rounded-md px-2 text-sm"
              >
                <option value="">None</option>
                {CALL_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            {filterCategory && (
              <div className="flex flex-col gap-1">
                <label className={label}>Value</label>
                <select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="input-embossed h-9 w-[220px] rounded-md px-2 text-sm"
                >
                  <option value="">All</option>
                  {filterValueOptions.map((v: any) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {filterCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterCategory('');
                  setFilterValue('');
                }}
              >
                Clear
              </Button>
            )}
          </div>

          <div className="mb-3 rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {loadingMore ? (
                <span>
                  Loading full data: <strong className="text-foreground">{progressText}</strong>
                </span>
              ) : stoppedAt ? (
                <>
                  <span>
                    Loading stopped at <strong className="text-foreground">{progressText}</strong>. Cards, charts and
                    table show loaded records only.
                  </span>
                  <Button size="sm" variant="outline" onClick={loader.resume}>
                    Resume loading
                  </Button>
                </>
              ) : (
                <span>
                  All <strong className="text-foreground">{allLoaded.toLocaleString()}</strong> matching records loaded
                </span>
              )}
              {browserFiltered && (
                <span>
                  · Showing <strong className="text-foreground">{filteredRows.length.toLocaleString()}</strong> of{' '}
                  {rows.length.toLocaleString()} loaded calls
                </span>
              )}
            </div>
            {(loadingMore || stoppedAt) && total ? (
              <div className="mt-1.5 h-1 w-full overflow-hidden rounded bg-muted">
                <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, (rows.length * 100) / total)}%` }} />
              </div>
            ) : null}
          </div>

          <div className="mb-4 grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
            {CARDS.map((card) => (
              <div key={card.key} className="rounded-lg border border-border/60 px-3 py-2.5">
                <div className="text-[10px] font-bold uppercase text-muted-foreground">{card.label}</div>
                <div className={'text-xl font-bold ' + card.className}>{stats[card.key].toLocaleString()}</div>
              </div>
            ))}
          </div>

          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call charts</span>
            <Button variant="ghost" size="sm" onClick={() => setShowCharts((v) => !v)}>
              {showCharts ? 'Hide charts' : 'Show charts'}
            </Button>
          </div>
          {showCharts ? (
            <div className="mb-4 grid gap-3 lg:grid-cols-2">
              <ReportChartCard title="Call status" subtitle="Connected, failed and abandoned calls">
                <DonutChart data={charts.status} centerLabel="Calls" emptyLabel="No calls for these filters" />
              </ReportChartCard>
              {lockedDirection ? null : (
                <ReportChartCard title="Direction" subtitle="Incoming vs outgoing">
                  <DonutChart data={charts.direction} centerLabel="Calls" emptyLabel="No calls for these filters" />
                </ReportChartCard>
              )}
              <ReportChartCard title="Customer talk time" subtitle="Calls by talk duration">
                <DonutChart data={charts.talkBuckets} centerLabel="Calls" emptyLabel="No calls for these filters" />
              </ReportChartCard>
              <ReportChartCard title="Calls per hour" subtitle="Hour of day across the selected range" className="lg:col-span-2">
                <ColumnChart data={charts.perHour} categoryKey="hour" stacked series={statusSeries} emptyLabel="No calls for these filters" />
              </ReportChartCard>
              {charts.perDay.length > 1 ? (
                <ReportChartCard title="Daily trend" subtitle="Calls per day" className="lg:col-span-2">
                  <ColumnChart data={charts.perDay} categoryKey="day" stacked series={statusSeries} />
                </ReportChartCard>
              ) : null}
              <ReportChartCard
                title="Calls by agent"
                subtitle={
                  charts.agentCount > charts.byAgent.length
                    ? `Top ${charts.byAgent.length} of ${charts.agentCount} agents`
                    : 'All agents in view'
                }
                actions={<TopNSelect value={topAgents} onChange={setTopAgents} label="Agents" />}
              >
                <HBarChart data={charts.byAgent} categoryKey="agent" stacked rowHeight={26} series={statusSeries} emptyLabel="No agent calls" />
              </ReportChartCard>
              <ReportChartCard
                title="Top call end reasons"
                subtitle={charts.reasonCount > 8 ? `Top 8 of ${charts.reasonCount}` : 'All end reasons'}
              >
                <HBarChart
                  data={charts.endReasons}
                  categoryKey="reason"
                  series={[{ key: 'calls', label: 'Calls', color: REPORT_COLORS.call }]}
                  emptyLabel="No call end reasons"
                />
              </ReportChartCard>
            </div>
          ) : null}
        </>
      )}

      <div className="scroll-container">
        <DataTable
          tableId={campaignMode ? 'campaign-call-report:v1' : 'call-report:v1'}
          pageSizeOptions={REPORT_PAGE_SIZES}
          columns={columns}
          data={filteredRows}
          pageSize={10}
          emptyTitle={
            formError
              ? formError
              : rows.length && !filteredRows.length
                ? 'No loaded calls match the call filter.'
                : pickDids && !rows.length && !selectedDids.length
                  ? 'Pick a date range (DIDs, agent or call ID optional) and press Search to load calls.'
                  : 'No calls found.'
          }
          // Export hidden on DID detail / CDR / campaign report for now; restore the line below to bring it back.
          // {...(role === USER_ROLES.CALL_CENTRE_AGENT ? { disableExport: true } : { onExport })}
          disableExport
        />
      </div>
      <CdrCallDetailDialog
        row={detailRow}
        did={
          (detailRow && (lockedDid || selectedDids.find((d) => numbersMatch(d, detailRow.did)) || detailRow.did || selectedDids[0])) || ''
        }
        pageRows={rows}
        userRole={role}
        hangupCauseMap={hangupCauseMap}
        transcriptLines={detailRow ? transcriptLineCount(detailRow.trace_id) : 0}
        onClose={() => setDetailRow(null)}
        traceUrlBase={traceUrlBase}
      />
    </div>
  );
}
